import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'

import api from '@/api/_legacy'
import {
  ApiAsset,
  ApiAssetDetailsApiPaginationResult,
  ApiDistributionAlert,
  ApiDistributionData,
  ApiExpectedReinvestmentData,
  ApiMutualFundAsset,
  ApiNewReinvestmentConfirmation,
  ApiReinvestmentConfirmation
} from '@/api/_legacy/generated-api'
import { defaultQueryParams, QueryParams } from '@/api/_legacy/types'
import { TablePaginationParams } from '@/components/core-v2/molecules/table-pagination'
import {
  ExDateDistribution,
  ExDateReinvestment,
  ExpectedReinvestmentData
} from '@/components/core-v2/organisms'
import { UserDisplayInfo } from '@/types/user'
import { formatToServerDateAsQueryString } from '@/utilities/date'
import { createLogoutExtraReducers } from '@/utilities/redux'

import { Errors, extractErrorMessagesFromServerResponse } from '../../tradebuilder-v2/util'
import { createEmptySchedule, ExDateSchedule, ExDateStatus } from '../types/exDateSchedule'
import {
  convertToApiDistribution,
  convertToApiReinvestment,
  updateScheduleReinvestments
} from '../types/mapper'

const TENANT_ID = 'absa'

export type FundsSearchFilter = {
  searchTerm: string
  showAll: boolean
  showRequiringAction: boolean
}

export type ExDateReinvestmentMapping = {
  confirmationId: string
  exDate: string
}

export type FundsState = {
  currentPageNumber: number
  distributions: ApiDistributionData[]
  errors?: any
  exDateNotification?: string
  exDateSaveErrors?: Errors
  exDateSchedules: ExDateSchedule[]
  expectedReinvestmentData: ExpectedReinvestmentData
  filter: FundsSearchFilter
  funds: ApiMutualFundAsset[]
  fundsAlerts: ApiDistributionAlert[]
  isLoading: boolean
  numTotalTrades: number
  query: QueryParams
  reinvestments: ApiReinvestmentConfirmation[]
  selectedExDate: string
  selectedFund: ApiMutualFundAsset
  unmappedReinvestments: ApiReinvestmentConfirmation[]
  updatedDistribution: ExDateDistribution
  updatedReinvestment: ExDateReinvestment
  userInfo: UserDisplayInfo
}

const initialState: FundsState = {
  numTotalTrades: 0,
  currentPageNumber: 1,
  funds: [],
  fundsAlerts: [],
  distributions: [],
  expectedReinvestmentData: undefined,
  reinvestments: [],
  unmappedReinvestments: [],
  exDateSchedules: [],
  updatedDistribution: undefined,
  updatedReinvestment: undefined,
  selectedFund: undefined,
  selectedExDate: undefined,
  query: { ...defaultQueryParams, PageSize: 20 },
  filter: { searchTerm: '', showAll: true, showRequiringAction: false },
  isLoading: false,
  userInfo: undefined
}

const hasMappedExDate = (reinvestment: ApiReinvestmentConfirmation) =>
  reinvestment.exDate && reinvestment.exDate !== null

export const fundsSlice = createSlice({
  name: 'funds',
  initialState,
  reducers: {
    init: (state, action: PayloadAction<UserDisplayInfo>) => {
      state.isLoading = true
      state.userInfo = action.payload
      state.exDateNotification = undefined
    },
    fetchFundsSuccess: (state, action: PayloadAction<ApiAssetDetailsApiPaginationResult>) => {
      state.funds = action.payload.data.map(item => item.asset)
      state.numTotalTrades = action.payload.totalDocuments
      state.isLoading = false
    },
    fetchFundsFailure: (state, action: PayloadAction<any>) => {
      state.isLoading = false
      state.errors = action.payload
    },
    fetchFundsAlertsSuccess: (state, action: PayloadAction<ApiDistributionAlert[]>) => {
      state.fundsAlerts = action.payload
    },
    fetchFundsAlertsFailure: (state, action: PayloadAction<any>) => {
      state.fundsAlerts = []
      state.errors = action.payload
    },
    setSelectedFund: (state, action: PayloadAction<ApiAsset>) => {
      state.selectedFund = action.payload
      state.selectedExDate = undefined

      if (state.selectedFund.exDates?.length !== 0) {
        const mappedSchedules = state.selectedFund.exDates.map(item => createEmptySchedule(item))
        state.exDateSchedules = mappedSchedules
      } else {
        state.exDateSchedules = []
      }
      state.exDateNotification = undefined
      state.distributions = []
      state.reinvestments = []
      state.unmappedReinvestments = []
      state.updatedReinvestment = undefined
      state.updatedDistribution = undefined
    },
    fetchDistributionSuccess: (state, action: PayloadAction<ApiDistributionData[]>) => {
      const distributions = action.payload
      state.distributions = distributions
      if (state.exDateSchedules.length > 0) {
        const clonedExDateSchedules = JSON.parse(
          JSON.stringify(state.exDateSchedules)
        ) as ExDateSchedule[]
        clonedExDateSchedules.forEach(clonedExDateSchedule => {
          const matchingDistributions = distributions.filter(
            item => item.exDistributionDate === clonedExDateSchedule.dateStr
          )
          if (matchingDistributions) {
            let hasAnyAbsaDistribution = false
            let hasAnyFinswitchDistribution = false
            matchingDistributions.forEach(matchingDistribution => {
              const isFinSwitchDistribution = matchingDistribution.providerType === 'FinSwitch'
              const baseDistribution = isFinSwitchDistribution
                ? clonedExDateSchedule.finSwitchDistribution
                : clonedExDateSchedule.finalDistribution
              if (!isFinSwitchDistribution && !hasAnyAbsaDistribution) {
                hasAnyAbsaDistribution = matchingDistribution.providerType === 'Absa'
              }
              if (isFinSwitchDistribution && !hasAnyFinswitchDistribution) {
                hasAnyFinswitchDistribution = true
              }

              switch (matchingDistribution.distributionType) {
                case 'Capital':
                  baseDistribution.capitalPerShare = matchingDistribution.amountPerShare
                  break
                case 'Dividend':
                  baseDistribution.dividendPerShare = matchingDistribution.amountPerShare
                  break
                case 'Interest':
                  baseDistribution.interestPerShare = matchingDistribution.amountPerShare
                  break
              }
              baseDistribution.totalPerShare =
                (baseDistribution.dividendPerShare ?? 0) +
                (baseDistribution.interestPerShare ?? 0) +
                (baseDistribution.capitalPerShare ?? 0)

              if (isFinSwitchDistribution) {
                clonedExDateSchedule.finSwitchDistribution = baseDistribution
              } else {
                clonedExDateSchedule.finalDistribution = baseDistribution
              }
            })
            if (!hasAnyAbsaDistribution) {
              // Absa should be overriden with what we got from finswitch, if no value from absa
              clonedExDateSchedule.finalDistribution = JSON.parse(
                JSON.stringify(clonedExDateSchedule.finSwitchDistribution)
              )
              clonedExDateSchedule.finalDistribution.readOnly = false
              if (hasAnyFinswitchDistribution) {
                clonedExDateSchedule.distributionStatus = ExDateStatus.FinSwitchReceived
              }
            } else {
              clonedExDateSchedule.finalDistribution.readOnly = true
              clonedExDateSchedule.distributionStatus = ExDateStatus.UserSubmitted
            }
          }
        })
        state.exDateSchedules = clonedExDateSchedules
      }
    },
    fetchDistributionsFailure: (state, action: PayloadAction<any>) => {
      state.updatedDistribution = undefined
      state.distributions = []
      state.errors = action.payload
    },
    fetchReinvestmentsSuccess: (state, action: PayloadAction<ApiReinvestmentConfirmation[]>) => {
      const reinvestments = action.payload
      state.reinvestments = reinvestments
      if (state.exDateSchedules.length > 0) {
        const mappedReinvestments = []
        const unmappedReinvestments = []
        reinvestments.forEach(reinvestment => {
          if (hasMappedExDate(reinvestment)) {
            mappedReinvestments.push(reinvestment)
          } else {
            unmappedReinvestments.push(reinvestment)
          }
        })
        if (unmappedReinvestments && unmappedReinvestments.length > 0) {
          state.unmappedReinvestments = unmappedReinvestments
        } else {
          state.unmappedReinvestments = []
        }
        if (mappedReinvestments && mappedReinvestments.length > 0) {
          const clonedExDateSchedules = JSON.parse(
            JSON.stringify(state.exDateSchedules)
          ) as ExDateSchedule[]
          state.exDateSchedules = updateScheduleReinvestments(
            clonedExDateSchedules,
            mappedReinvestments
          )
        }
      }
    },
    fetchReinvestmentsFailure: (state, action: PayloadAction<any>) => {
      state.updatedReinvestment = undefined
      state.errors = action.payload
    },
    fetchExpectedReinvestmentsSuccess: (
      state,
      action: PayloadAction<ApiExpectedReinvestmentData>
    ) => {
      const { Capital = 0, Dividend = 0, Interest = 0 } = action.payload.scaledDistributions
      state.expectedReinvestmentData = {
        expectedUnits: action.payload.expectedReinvestmentUnits,
        totalDistribution: action.payload.totalDistributionAmount,
        centsPerUnit: Capital + Dividend + Interest,
        referenceHoldings: action.payload.referenceHoldingUnits
      }
    },
    fetchExpectedReinvestmentsFailure: (state, action: PayloadAction<any>) => {
      state.expectedReinvestmentData = undefined
      state.errors = action.payload
    },
    setSelectedExDate: (state, action: PayloadAction<string>) => {
      state.selectedExDate = action.payload
      state.exDateNotification = undefined
      state.exDateSaveErrors = undefined
    },
    setDistribution: (state, action: PayloadAction<ExDateDistribution>) => {
      state.updatedDistribution = action.payload
    },
    setReinvestment: (state, action: PayloadAction<ExDateReinvestment>) => {
      state.updatedReinvestment = action.payload
    },
    assignReinvestmentToDate: (state, action: PayloadAction<ExDateReinvestmentMapping>) => {
      const clonedExDateSchedules = state.exDateSchedules

      // Unmap if there is any existing confirmationId mapped
      clonedExDateSchedules.forEach(
        exDate => (exDate.finalReinvestment.mappedConfirmationId = undefined)
      )
      const mappedExDateSchedule = state.exDateSchedules.find(
        item => item.dateStr === action.payload.exDate
      )
      const mappedReinvestment = state.unmappedReinvestments.find(
        item => item.confirmationId === action.payload.confirmationId
      )
      if (mappedExDateSchedule && mappedReinvestment) {
        mappedExDateSchedule.finSwitchReinvestment.numberOfUnits = mappedReinvestment.shares
        mappedExDateSchedule.finSwitchReinvestment.reinvestmentDate =
          mappedReinvestment.reinvestmentDate
        mappedExDateSchedule.finalReinvestment.numberOfUnits = mappedReinvestment.shares
        mappedExDateSchedule.finalReinvestment.reinvestmentDate =
          mappedReinvestment.reinvestmentDate
        mappedExDateSchedule.finalReinvestment.mappedConfirmationId =
          mappedReinvestment.confirmationId

        state.exDateSchedules = [...clonedExDateSchedules]
      } else {
        state.errors.InvalidMapping = 'Failed to assign Finswitch reinvestment to selected date.'
      }
    },
    saveChangesSuccess: (state, action: PayloadAction<string>) => {
      state.exDateSaveErrors = undefined
      state.exDateNotification = action.payload
      // reset previous distributions/reinvestments and wait for fresh data from api
      state.distributions = []
      state.reinvestments = []
      state.unmappedReinvestments = []
      state.updatedReinvestment = undefined
      state.updatedDistribution = undefined
    },
    saveChangesFailure: (state, action: PayloadAction<Errors>) => {
      state.exDateSaveErrors = action.payload
      state.exDateNotification = undefined
    },
    resetExDateNotification: state => {
      state.exDateNotification = undefined
    },
    setServerFilter: (state, action: PayloadAction<FundsSearchFilter>) => {
      state.isLoading = true
      state.funds = []
      state.query.PageNumber = 1

      const filter = action.payload
      if (filter.searchTerm) {
        state.query.WildcardString = filter.searchTerm
      } else {
        state.query.WildcardString = undefined
      }

      state.filter = filter
    },

    setPage: (state, action: PayloadAction<TablePaginationParams>) => {
      state.query.PageNumber = action.payload.pageNumber
      state.query.PageSize = action.payload.pageSize
      state.isLoading = true
    },
    clearFilters: state => {
      state.query.Status = ''
      state.query.WildcardString = ''
    }
  },
  extraReducers: builder => {
    const logoutReducers = createLogoutExtraReducers(initialState)
    Object.entries(logoutReducers).forEach(([actionType, reducer]) => {
      builder.addCase(actionType, reducer)
    })
  }
})

export const { actions } = fundsSlice
export type FundsActions = typeof actions

function* fetchFundsSaga() {
  const rootState = yield select()
  const state = rootState.funds
  const { query } = state

  try {
    const response = yield call(api.tenant.listDetail, TENANT_ID, query)
    yield put({
      type: 'funds/fetchFundsSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'funds/fetchFundsFailure', payload: error })
  }
}

function* fetchFundsAlertsSaga() {
  try {
    const response = yield call(api.tenant.assetswithalertsDetail, TENANT_ID)
    yield put({
      type: 'funds/fetchFundsAlertsSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'funds/fetchFundsAlertsFailure', payload: error })
  }
}

function* fetchDistributionsSaga() {
  const rootState = yield select()
  const selectedAssetId = rootState.funds.selectedFund.assetId

  try {
    const response = yield call(api.tenant.distributionsDetail, selectedAssetId, TENANT_ID)
    yield put({
      type: 'funds/fetchDistributionSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'funds/fetchDistributionsFailure', payload: error })
  }
}

function* fetchReinvestmentsSaga() {
  const rootState = yield select()
  const selectedAssetId = rootState.funds.selectedFund.assetId

  try {
    const response = yield call(api.tenant.reinvestmentsDetail, selectedAssetId, TENANT_ID)
    yield put({
      type: 'funds/fetchReinvestmentsSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'funds/fetchReinvestmentsFailure', payload: error })
  }
}

function* fetchExpectedReinvestmentDataSaga() {
  const rootState = yield select()
  const selectedAssetId = rootState.funds.selectedFund.assetId
  const { selectedExDate } = rootState.funds

  if (!selectedExDate) return

  const exDate = new Date(selectedExDate)

  try {
    const response = yield call(
      api.tenant.reinvestmentsDataDetail,
      selectedAssetId,
      `${exDate.getFullYear()}-${exDate.getMonth() + 1}-${exDate.getDate()}`,
      TENANT_ID
    )
    yield put({
      type: 'funds/fetchExpectedReinvestmentsSuccess',
      payload: response.data
    })
  } catch (error) {
    if (error?.status === 404) {
      yield put({
        type: 'funds/fetchExpectedReinvestmentsFailure',
        payload: {
          message:
            'Distribution data is currently unavailable since the ex-date has not yet occurred.'
        }
      })
      return
    }
    if (error?.status === 500 || error?.status === 400) {
      yield put({
        type: 'funds/fetchExpectedReinvestmentsFailure',
        payload: extractErrorMessagesFromServerResponse(error)
      })
      return
    }

    yield put({ type: 'funds/fetchExpectedReinvestmentsFailure', payload: error })
  }
}

function* saveDistributionsSaga() {
  const rootState = yield select()
  const state = rootState.funds

  try {
    const distributions: ApiDistributionData[] = convertToApiDistribution(
      state.selectedFund.assetId,
      state.selectedExDate,
      state.updatedDistribution
    )
    yield call(api.tenant.postTenant3, TENANT_ID, distributions)
    yield put({
      type: 'funds/saveChangesSuccess',
      payload: 'Distributions submitted'
    })
  } catch (error) {
    yield put({
      type: 'funds/saveChangesFailure',
      payload: extractErrorMessagesFromServerResponse(error)
    })
  }
}

function* saveReinvestmentsSaga() {
  const rootState = yield select()
  const state = rootState.funds
  const update: ApiNewReinvestmentConfirmation = convertToApiReinvestment(
    state.selectedFund.assetId,
    state.selectedExDate,
    state.updatedReinvestment
  )
  try {
    // check if reinvestment also had a mapped confirmation Id
    if (state.updatedReinvestment.mappedConfirmationId) {
      yield call(
        api.tenant.reinvestmentsAssignUpdate,
        state.updatedReinvestment.mappedConfirmationId,
        formatToServerDateAsQueryString(state.selectedExDate),
        TENANT_ID
      )
    }
    yield call(api.tenant.reinvestmentsAddCreate, TENANT_ID, update)

    yield put({
      type: 'funds/saveChangesSuccess',
      payload: 'Reinvestment confirmation submitted'
    })
  } catch (error) {
    yield put({
      type: 'funds/saveChangesFailure',
      payload: extractErrorMessagesFromServerResponse(error)
    })
  }
}

export function* fundsRootSaga() {
  yield all([
    takeLatest('funds/init', fetchFundsSaga),
    takeLatest('funds/init', fetchFundsAlertsSaga),
    takeLatest('funds/search', fetchFundsSaga),
    takeLatest('funds/setServerFilter', fetchFundsSaga),
    takeLatest('funds/setPage', fetchFundsSaga),
    takeLatest('funds/setSelectedExDate', fetchExpectedReinvestmentDataSaga),
    takeLatest('funds/setSelectedFund', fetchDistributionsSaga),
    takeLatest('funds/setSelectedFund', fetchReinvestmentsSaga),
    takeLatest('funds/setDistribution', saveDistributionsSaga),
    takeLatest('funds/setReinvestment', saveReinvestmentsSaga),
    takeLatest('funds/saveChangesSuccess', fetchDistributionsSaga),
    takeLatest('funds/saveChangesSuccess', fetchReinvestmentsSaga)
  ])
}

export default fundsSlice.reducer
